import { createSlice, createSelector } from '@reduxjs/toolkit';
import { RootState } from 'state/store';

type PsrItem = {
  code: string;
  subCode?: string;
  expiryDate?: string;
};

export type PsrPreferences = {
  [type: string]: PsrItem[];
};

export type PsrState = {
  psrPreferences: PsrPreferences;
  hasPsr: boolean | null;
};

export const initialState: PsrState = {
  psrPreferences: {},
  hasPsr: null
};

export const slice = createSlice({
  name: 'psr',
  initialState,
  reducers: {
    setInitialState: (state) => {
      state.psrPreferences = initialState.psrPreferences;
      state.hasPsr = initialState.hasPsr;
    },
    setHasPsr: (state, { payload = false }) => {
      state.hasPsr = payload;
    },
    setPsrPreferences: (state, { payload }) => {
      const { psrItem, checked, type } = payload;

      if (checked) {
        state.psrPreferences[type] = [
          ...(state.psrPreferences[type] ?? []),
          psrItem
        ];
      } else {
        state.psrPreferences[type] = state.psrPreferences[type]?.filter(
          (psrOption) => {
            const toBeRemoved =
              psrOption.code === psrItem.code &&
              psrOption.subCode === psrItem.subCode;

            return !toBeRemoved;
          }
        );
      }
    },
    resetPsrPreferences: (state) => {
      state.psrPreferences = initialState.psrPreferences;
    },
    setPsrSubCode: (state, { payload }) => {
      const { psrItem, type } = payload;
      const { code, subCode } = psrItem;

      const psrIndex = state.psrPreferences[type]?.findIndex(
        (item) => item.code === code
      );

      const psrItemToUpdate = state.psrPreferences[type][psrIndex];

      state.psrPreferences[type][psrIndex] = {
        ...psrItemToUpdate,
        subCode
      };
    },
    setPsrExpiryDate: (state, { payload }) => {
      const { code, subCode, expiryDate, type } = payload;

      const index = state.psrPreferences[type].findIndex(
        (item) => item.code === code && item.subCode === subCode
      );

      state.psrPreferences[type][index] = {
        ...state.psrPreferences[type][index],
        expiryDate
      };
    }
  }
});

export const {
  setInitialState,
  setHasPsr,
  setPsrPreferences,
  setPsrExpiryDate,
  setPsrSubCode,
  resetPsrPreferences
} = slice.actions;

export const selectSlice = (state: RootState) => state[slice.name];
export const selectHasPsr = (state: RootState) => selectSlice(state).hasPsr;
export const selectPsrPreferences = (state: RootState) =>
  selectSlice(state).psrPreferences;

export const selectPsrPreferencesArr = createSelector(
  selectPsrPreferences,
  (psrPreferences: PsrPreferences): (PsrItem & { type: string })[] => {
    return Object.entries(psrPreferences).reduce<
      (PsrItem & { type: string })[]
    >((acc, [key, val]) => {
      const typedValues = val.map((v) => ({ ...v, type: key }));
      return [...acc, ...typedValues];
    }, []);
  }
);

export default slice.reducer;
